import { sql } from 'drizzle-orm';
import {
  boolean,
  index,
  pgEnum,
  pgTable,
  text,
  timestamp,
  unique,
  uniqueIndex,
  uuid
} from 'drizzle-orm/pg-core';

import { applications } from './applications';
import { candidates } from './candidates';
import { interviewInvites } from './interview-invites';
import { interviewQuestions, interviews } from './interviews';
import { organizations } from './organizations';
import { tenantOrgIsolationPolicy } from './rls';
import { tenants } from './tenants';

export const interviewSessionStatusEnum = pgEnum('interview_session_status', [
  'in_progress',
  'completed',
  'failed',
  'restarted',
  'abandoned'
]);

export const interviewMessageRoleEnum = pgEnum('interview_message_role', [
  'assistant',
  'candidate'
]);

export type InterviewMessageRole = (typeof interviewMessageRoleEnum.enumValues)[number];

export const interviewSessions = pgTable(
  'interview_sessions',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id),
    orgId: uuid('org_id')
      .notNull()
      .references(() => organizations.id),
    interviewId: uuid('interview_id')
      .notNull()
      .references(() => interviews.id),
    candidateId: uuid('candidate_id')
      .notNull()
      .references(() => candidates.id),
    inviteId: uuid('invite_id')
      .notNull()
      .references(() => interviewInvites.id),
    status: interviewSessionStatusEnum('status').notNull().default('in_progress'),
    language: text('language'),
    modality: text('modality').notNull().default('text'),
    livekitRoomName: text('livekit_room_name'),
    recordingEgressId: text('recording_egress_id'),
    recordingUrl: text('recording_url'),
    startedAt: timestamp('started_at', { withTimezone: true }),
    completedAt: timestamp('completed_at', { withTimezone: true }),
    failedAt: timestamp('failed_at', { withTimezone: true }),
    restartedAt: timestamp('restarted_at', { withTimezone: true }),
    abandonedAt: timestamp('abandoned_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdateFn(() => new Date())
  },
  (t) => [
    index('interview_sessions_interview_id_idx').on(t.interviewId),
    index('interview_sessions_invite_id_idx').on(t.inviteId),
    // Prevent duplicate active sessions per invite — only one non-failed session allowed at a time.
    // onConflictDoNothing() on insert relies on this constraint to detect concurrent starts.
    uniqueIndex('interview_sessions_invite_id_active_uidx')
      .on(t.inviteId)
      .where(sql`status IN ('in_progress', 'completed')`),
    tenantOrgIsolationPolicy()
  ]
).enableRLS();

export const interviewMessages = pgTable(
  'interview_messages',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id),
    orgId: uuid('org_id')
      .notNull()
      .references(() => organizations.id),
    sessionId: uuid('session_id')
      .notNull()
      .references(() => interviewSessions.id),
    role: interviewMessageRoleEnum('role').notNull(),
    content: text('content').notNull(),
    questionId: uuid('question_id').references(() => interviewQuestions.id),
    copyPasteDetected: boolean('copy_paste_detected').notNull().default(false),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow()
  },
  () => [tenantOrgIsolationPolicy()]
).enableRLS();

export const applicationInterviewSessions = pgTable(
  'application_interview_sessions',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id),
    orgId: uuid('org_id')
      .notNull()
      .references(() => organizations.id),
    applicationId: uuid('application_id')
      .notNull()
      .references(() => applications.id),
    sessionId: uuid('session_id')
      .notNull()
      .references(() => interviewSessions.id),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow()
  },
  (t) => [
    unique('application_interview_sessions_app_session_uidx').on(t.applicationId, t.sessionId),
    index('application_interview_sessions_application_id_idx').on(t.applicationId),
    index('application_interview_sessions_session_id_idx').on(t.sessionId),
    tenantOrgIsolationPolicy()
  ]
).enableRLS();
